<script>
import { mapActions, mapGetters } from 'vuex'
import toggleArrayValue from '@/utils/toggle-array-value'
import accessibleNavigationMixin from '@/mixins/accessibleNavigationMixin'
import HeightTransition from '@/components/general/height-transition.vue'
import AzureFeatureFlags from '@/shared/constants/features/AzureFeatureFlags'
import {
  FilterViews,
  SortOptions,
  SortDirection,
} from '@/shared/constants/candidate/CandidateListFilterMenu'
import CandidateFilterMenuItem from './candidate-filter-menu-item.vue'

const { SelfServiceReleaseBookable } = AzureFeatureFlags

export default {
  name: 'CandidateFilterMenu',
  components: {
    HeightTransition,
    CandidateFilterMenuItem,
  },
  mixins: [accessibleNavigationMixin],
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      view: null,
      menuOpen: false,
      errors: {
        certifications: false,
        types: false,
      },
      loading: {
        certifications: false,
        types: false,
      },
      cachedSubviewItems: [],
      subviewItemsOriginalOrder: [],
      stagedFilters: {
        bookable: null,
        certificationIds: [],
        clientGroupIds: [],
        typeIds: [],
      },
      stagedSortKey: null,
      stagedSortDir: SortDirection.Desc,
      certifications: [],
      types: [],
    }
  },
  computed: {
    ...mapGetters({
      candidateListFilters: 'candidate/candidateListFilters',
      selectedClientIds: 'client/getSelectedClients',
      allClientGroups: 'auth/getAllClientGroups',
      isFeatureEnabled: 'features/isFeatureEnabled',
    }),
    clientGroups() {
      const selectedIds = new Set(this.selectedClientIds)

      return (this.allClientGroups ?? [])
        .filter((group) =>
          group.clients?.some((client) => selectedIds.has(client.clientId))
        )
        .map(({ groupId: id, groupName: title }) => ({
          id,
          title,
        }))
    },
    activeFilterCount() {
      const {
        sortKey,
        bookable,
        clientGroupIds,
        certificationIds,
        typeIds,
      } = this.candidateListFilters

      return (
        (sortKey ? 1 : 0) +
        (bookable != null ? 1 : 0) +
        clientGroupIds.length +
        certificationIds.length +
        typeIds.length
      )
    },
    hasActiveFilters() {
      return this.activeFilterCount > 0
    },
    filterButtonLabel() {
      return this.hasActiveFilters
        ? this.$t('candidateList.filters.labelPlural')
        : this.$t('candidateList.filters.label')
    },
    isRootView() {
      return this.view === null
    },
    showBookableFilter() {
      return this.isFeatureEnabled(SelfServiceReleaseBookable)
    },
    viewTransitionName() {
      if (!this.menuOpen) return ''
      return this.isRootView ? 'slide-right' : 'slide-left'
    },
    filterScreens() {
      return {
        [FilterViews.Sort]: {
          title: this.$t('candidateList.sort.label'),
          items: SortOptions,
        },
        [FilterViews.ClientGroups]: {
          title: this.$t('candidateList.filters.clientGroups'),
          items: this.clientGroups,
          keyPrefix: 'client-group',
          selectedIds: this.stagedFilters.clientGroupIds,
        },
        [FilterViews.Certifications]: {
          title: this.$t('candidateList.filters.certifications'),
          items: this.certifications,
          keyPrefix: 'certification',
          selectedIds: this.stagedFilters.certificationIds,
        },
        [FilterViews.Types]: {
          title: this.$t('candidateList.filters.types'),
          items: this.types,
          keyPrefix: 'type',
          selectedIds: this.stagedFilters.typeIds,
        },
      }
    },
    activeFilterScreen() {
      if (!this.isRootView) return this.filterScreens[this.view]

      const { certificationIds, clientGroupIds, typeIds } = this.stagedFilters

      const rootFilterMenuItems = [
        {
          view: FilterViews.Sort,
          icon: 'mdi-swap-vertical',
          title: this.$t('candidateList.sort.label'),
          count: this.stagedSortKey ? 1 : 0,
        },
        {
          view: FilterViews.Types,
          icon: 'mdi-account-outline',
          title: this.$t('candidateList.filters.types'),
          count: typeIds.length,
          disabled: this.loading.types,
        },
        {
          type: 'switch',
          icon: 'mdi-check-circle-outline',
          title: this.$t('candidateList.filters.bookable'),
          switchValue: !!this.stagedFilters.bookable,
          hidden: !this.showBookableFilter,
        },
        {
          view: FilterViews.ClientGroups,
          icon: 'mdi-home-outline',
          title: this.$t('candidateList.filters.clientGroups'),
          count: clientGroupIds.length,
          hidden: this.clientGroups.length <= 1,
        },
        {
          view: FilterViews.Certifications,
          icon: 'mdi-medal-outline',
          title: this.$t('candidateList.filters.certifications'),
          count: certificationIds.length,
          disabled: this.loading.certifications,
        },
      ].filter(({ hidden = false }) => !hidden)

      return {
        title: this.$t('candidateList.filters.label'),
        items: rootFilterMenuItems,
      }
    },
  },
  watch: {
    selectedClientIds(newIds, oldIds) {
      if (oldIds === undefined) return

      if (
        newIds.length === oldIds.length &&
        newIds.every((id, i) => id === oldIds[i])
      ) {
        return
      }

      this.resetAsyncFilterData()

      if (this.menuOpen) this.syncStagedFromStore()
    },
    menuOpen(open) {
      if (open) {
        this.syncStagedFromStore()
        this.accNavFocusedIndex = 0
        return
      }

      this.view = null
      this.$nextTick(() => this.$refs.filterButton?.$el?.blur())
    },
  },
  mounted() {
    this.getFeatureFlag(SelfServiceReleaseBookable)
  },
  methods: {
    ...mapActions({
      setCandidateListFilters: 'candidate/setCandidateListFilters',
      getCandidateListCertifications:
        'candidate/getCandidateListCertifications',
      getCandidateListTypes: 'candidate/getCandidateListTypes',
      getFeatureFlag: 'features/getFeatureFlag',
    }),
    syncStagedFromStore() {
      const {
        bookable,
        certificationIds,
        clientGroupIds,
        typeIds,
        sortKey,
        sortDir,
      } = this.candidateListFilters

      this.stagedFilters = {
        bookable,
        certificationIds: [...certificationIds],
        clientGroupIds: [...clientGroupIds],
        typeIds: [...typeIds],
      }
      this.stagedSortKey = sortKey ?? null
      this.stagedSortDir = sortDir ?? SortDirection.Desc
    },
    selectSort(option) {
      if (this.stagedSortKey !== option.key) {
        this.stagedSortKey = option.key
        this.stagedSortDir = SortDirection.Desc
        return
      }

      if (this.stagedSortDir === SortDirection.Desc) {
        this.stagedSortDir = SortDirection.Asc
        return
      }

      this.stagedSortKey = null
      this.stagedSortDir = SortDirection.Desc
    },
    isSortSelected(option) {
      return this.stagedSortKey === option.key
    },
    toggleFilter(id) {
      toggleArrayValue(this.activeFilterScreen.selectedIds, id)
    },
    applyFilters() {
      const {
        bookable,
        certificationIds,
        clientGroupIds,
        typeIds,
      } = this.stagedFilters

      this.setCandidateListFilters({
        sortKey: this.stagedSortKey,
        sortDir: this.stagedSortDir,
        bookable,
        certificationIds: [...certificationIds],
        clientGroupIds: [...clientGroupIds],
        typeIds: [...typeIds],
      })

      this.menuOpen = false
    },
    clearAll() {
      this.setCandidateListFilters({
        sortKey: null,
        sortDir: SortDirection.Asc,
        bookable: null,
        certificationIds: [],
        clientGroupIds: [],
        typeIds: [],
      })

      this.menuOpen = false
      this.view = null
    },
    clearCurrentView() {
      if (this.view === FilterViews.Sort) {
        this.stagedSortKey = null
        this.stagedSortDir = SortDirection.Desc
        return
      }

      const filterKeyMap = {
        [FilterViews.ClientGroups]: 'clientGroupIds',
        [FilterViews.Certifications]: 'certificationIds',
        [FilterViews.Types]: 'typeIds',
      }

      const key = filterKeyMap[this.view]
      if (!key) return

      this.stagedFilters[key] = []
      this.cachedSubviewItems = [...this.subviewItemsOriginalOrder]
    },
    handleMenuItemClick(item) {
      if (item.type === 'switch') {
        this.stagedFilters.bookable = this.stagedFilters.bookable ? null : true
        return
      }

      if (item.view) this.navigateToView(item.view)
    },
    async navigateToView(targetView) {
      if (Object.values(this.loading).some(Boolean)) return

      if (targetView === FilterViews.Sort) {
        this.view = FilterViews.Sort
        return
      }

      const asyncFilterActions = {
        [FilterViews.Certifications]: this.getCandidateListCertifications,
        [FilterViews.Types]: this.getCandidateListTypes,
      }

      const action = asyncFilterActions[targetView]

      if (action && !(await this.loadAsyncFilter(targetView, action))) return

      const { items, selectedIds } = this.filterScreens[targetView]

      this.subviewItemsOriginalOrder = [...items]

      this.cachedSubviewItems = [...items].sort((a, b) => {
        const aSelected = selectedIds.includes(a.id)
        const bSelected = selectedIds.includes(b.id)

        if (aSelected === bSelected) return 0

        return aSelected ? -1 : 1
      })

      this.view = targetView
    },
    async loadAsyncFilter(view, action) {
      if (this[view].length) return true

      this.setAsyncFilterLoadingState(view)

      const { data, error } = await action(this.selectedClientIds)

      this.loading[view] = false

      if (error) {
        this.errors[view] = true
        return false
      }

      this[view] = data?.[view] ?? []

      return this[view].length > 0
    },
    resetAsyncFilterData() {
      const { Certifications, Types } = FilterViews

      ;[Certifications, Types].forEach((view) => {
        this[view] = []
        this.errors[view] = false
        this.loading[view] = false
      })
    },
    setAsyncFilterLoadingState(filterView) {
      this.loading[filterView] = true
      this.errors[filterView] = false
    },
  },
}
</script>

<template>
  <v-menu
    v-model="menuOpen"
    :close-on-content-click="false"
    offset-y
    bottom
    left
    min-width="320"
    max-width="360"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-btn
        ref="filterButton"
        class="candidate-filter-menu__button rounded-lg"
        :class="{ 'candidate-filter-menu__button--pending': disabled }"
        :style="{ width: hasActiveFilters ? '140px' : '90px' }"
        height="40"
        outlined
        :color="hasActiveFilters || menuOpen ? 'primary' : undefined"
        v-bind="attrs"
        v-on="on"
      >
        <v-icon left>mdi-tune</v-icon>
        {{ filterButtonLabel }}
        <v-badge v-if="hasActiveFilters" color="primary" class="ml-2" inline>
          <template v-slot:badge>
            <transition name="fade-cycle-down" mode="out-in">
              <span :key="activeFilterCount">{{ activeFilterCount }}</span>
            </transition>
          </template>
        </v-badge>
      </v-btn>
    </template>

    <v-card class="candidate-filter-menu">
      <div>
        <transition name="fade" mode="out-in">
          <v-card-title
            :key="view || 'root'"
            class="candidate-filter-menu__title d-flex align-center pb-2"
          >
            <v-btn
              v-if="!isRootView"
              icon
              small
              class="mr-2"
              @click="view = null"
            >
              <v-icon>mdi-arrow-left</v-icon>
            </v-btn>
            <span class="text-subtitle-1 font-weight-bold">
              {{ activeFilterScreen.title }}
            </span>
          </v-card-title>
        </transition>
      </div>

      <HeightTransition
        class="candidate-filter-menu__body"
        :content-transition="viewTransitionName"
      >
        <v-list
          v-if="isRootView"
          :key="view || 'root'"
          dense
          class="candidate-filter-menu__view"
          @keydown="onAccNavKeydown"
        >
          <CandidateFilterMenuItem
            v-for="(item, index) in activeFilterScreen.items"
            :key="item.view || item.title"
            ref="accNavItems"
            :error="errors[item.view]"
            :loading="loading[item.view]"
            :disabled="item.disabled"
            :icon="item.icon"
            :title="item.title"
            :count="item.count"
            :type="item.type"
            :switch-value="item.switchValue"
            :tabindex="index === accNavFocusedIndex ? 0 : -1"
            @click="handleMenuItemClick(item)"
          />
        </v-list>

        <v-list
          v-else-if="view === 'sort'"
          key="sort"
          dense
          class="candidate-filter-menu__view"
        >
          <CandidateFilterMenuItem
            v-for="option in activeFilterScreen.items"
            :key="`sort-option-${option.key}`"
            :title="$t(option.i18nKey)"
            type="sort"
            :selected="isSortSelected(option)"
            :sort-dir="stagedSortDir"
            @click="selectSort(option)"
          />
        </v-list>

        <v-list
          v-else-if="activeFilterScreen"
          :key="view"
          dense
          max-height="320"
          class="candidate-filter-menu__view overflow-y-auto"
          @keydown="onAccNavKeydown"
        >
          <transition-group name="list-item" tag="div">
            <CandidateFilterMenuItem
              v-for="(item, index) in cachedSubviewItems"
              :key="`${activeFilterScreen.keyPrefix}-${item.id}`"
              ref="accNavItems"
              :title="item.title"
              :tabindex="index === accNavFocusedIndex ? 0 : -1"
              action-position="prepend"
              action-class="mr-3"
              :selected="activeFilterScreen.selectedIds.includes(item.id)"
              @click="toggleFilter(item.id)"
              @change="toggleFilter(item.id)"
            />
          </transition-group>
        </v-list>
      </HeightTransition>

      <v-divider />

      <v-card-actions class="d-flex justify-space-between pa-3">
        <v-btn
          text
          color="primary"
          :disabled="disabled"
          @click="isRootView ? clearAll() : clearCurrentView()"
        >
          {{
            isRootView
              ? $t('candidateList.filters.clearAll')
              : $t('candidateList.filters.clear')
          }}
        </v-btn>
        <v-btn
          color="primary"
          depressed
          class="rounded-lg px-4"
          :disabled="disabled"
          @click="applyFilters"
        >
          {{ $t('candidateList.filters.apply') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-menu>
</template>

<style lang="scss" scoped>
@import '@/styles/_colors.scss';
@import '@/styles/_vue-transitions.scss';

.candidate-filter-menu__button.v-btn--outlined:not(.primary--text) {
  color: $color-gray-600 !important;
  border-color: $color-neutral-grey-200 !important;

  ::v-deep .v-icon {
    color: $color-neutral-grey-400;
  }

  &:hover,
  &:active,
  &.v-btn--active {
    color: $color-blue-500 !important;
    background-color: $color-info-270 !important;
    border-color: $color-info-300 !important;

    ::v-deep .v-icon {
      color: $color-blue-400 !important;
    }
  }
}

.candidate-filter-menu {
  border-radius: 12px;
}

.candidate-filter-menu__button--pending {
  pointer-events: none;
  opacity: 0.5;
}

.candidate-filter-menu__button {
  min-width: unset !important;
  transition: width 0.25s ease !important;

  ::v-deep .v-badge__badge {
    span {
      display: inline-block;
    }
  }
}
</style>
